// One note on the scripture above it, set off as an indented aside so it
// reads as commentary rather than another paragraph of the passage.
import type { CommentaryNoteInput } from "@bibleunleashed/shared";
import { Icon } from "@/components/Icon";
import { Editable } from "./Editable";
import { VerseRangeField } from "./VerseRangeField";

/** "v. 4" for one verse, "vv. 5-10" for a run, nothing for a note on no verse. */
export function verseLabel(startVerse: number | null, endVerse: number | null): string {
  if (startVerse === null) return "";
  return endVerse && endVerse > startVerse ? `vv. ${startVerse}-${endVerse}` : `v. ${startVerse}`;
}

export function NoteBlock({
  note,
  label,
  editing,
  onChange,
  onMove,
  onRemove,
  canMoveUp,
  canMoveDown,
}: {
  note: CommentaryNoteInput;
  label: string;
  editing: boolean;
  onChange: (change: Partial<CommentaryNoteInput>) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const marker = verseLabel(note.startVerse, note.endVerse);
  if (!editing && !note.bodyMd.trim()) return null;

  return (
    <div
      className={`ml-2 rounded-r-md border-l-2 py-2.5 pr-4 pl-4 lg:ml-6 ${
        editing ? "border-dashed border-rule bg-transparent" : "border-rule bg-paper-2"
      }`}
    >
      {editing ? (
        <div className="mb-2 flex items-center gap-2">
          <VerseRangeField startVerse={note.startVerse} endVerse={note.endVerse} onChange={onChange} />
          <span className="ml-auto flex items-center gap-0.5">
            <NoteButton label={`Move ${label} up`} icon="up" disabled={!canMoveUp} onClick={() => onMove(-1)} />
            <NoteButton label={`Move ${label} down`} icon="down" disabled={!canMoveDown} onClick={() => onMove(1)} />
            <NoteButton label={`Delete ${label}`} icon="trash" onClick={onRemove} />
          </span>
        </div>
      ) : (
        marker && <p className="mb-1 font-sans text-[11px] tracking-[0.06em] text-accent uppercase">{marker}</p>
      )}
      <div className="font-serif text-[16.5px] leading-[1.6]">
        <Editable
          label={label}
          editing={editing}
          value={note.bodyMd}
          placeholder="What these verses are saying…"
          onChange={(bodyMd) => onChange({ bodyMd })}
        />
      </div>
    </div>
  );
}

function NoteButton({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: "up" | "down" | "trash";
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="grid size-6 place-items-center rounded text-ink-3 hover:bg-paper-3 hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
    >
      <Icon name={icon} width="14" height="14" />
    </button>
  );
}
